import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Star, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const PLATFORMS = ["Trustpilot", "Facebook", "Google"];
const EMPTY = { reviewer_name: "", platform: "Trustpilot", rating: 5, text: "", date: "" };

function Stars({ rating }) {
  return (
    <span className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= rating ? "fill-[#1A237E] text-[#1A237E]" : "text-slate-300"}`} />
      ))}
    </span>
  );
}

function ReviewDialog({ review, onClose, onSaved }) {
  const [form, setForm] = useState(review ? { ...EMPTY, ...review } : EMPTY);
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1A237E]";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, rating: Number(form.rating) };
    try {
      if (review) {
        await api.put(`/admin/reviews/${review.id}`, payload);
        toast.success("Review updated");
      } else {
        await api.post("/admin/reviews", payload);
        toast.success("Review added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="review-form-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{review ? "Edit Review" : "Add Review"}</h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid="review-dialog-close"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reviewer Name *</label>
            <input required value={form.reviewer_name} onChange={(e) => setForm((f) => ({ ...f, reviewer_name: e.target.value }))} className={inputCls} data-testid="review-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Platform</label>
              <select value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} className={inputCls} data-testid="review-platform-select">
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Star Rating</label>
              <select value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))} className={inputCls} data-testid="review-rating-select">
                {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{`${r} star${r > 1 ? "s" : ""}`}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date (e.g. June 2023)</label>
            <input value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} data-testid="review-date-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Review Text</label>
            <textarea rows={4} value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} className={inputCls} data-testid="review-text-input" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="review-cancel-button">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-[#1A237E] rounded-md hover:bg-[#121A5C] disabled:opacity-60" data-testid="review-save-button">
              {saving ? "Saving…" : review ? "Save Changes" : "Add Review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const COLUMNS = [
  { key: "reviewer_name", label: "Reviewer" },
  { key: "platform", label: "Platform" },
  { key: "rating", label: "Rating" },
  { key: "date", label: "Date" },
];

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [sort, setSort] = useState({ key: "created_at", dir: -1 });

  const load = () => {
    api.get("/reviews").then((res) => setReviews(res.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete review by ${r.reviewer_name}?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const sorted = [...reviews].sort((a, b) => {
    const av = a[sort.key] ?? "";
    const bv = b[sort.key] ?? "";
    const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
    return cmp * sort.dir;
  });

  return (
    <div data-testid="review-tracker-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Review Tracker</h1>
          <p className="text-sm text-slate-500 mt-1">{reviews.length} reviews logged — shown live on the public Testimonials page</p>
        </div>
        <button onClick={() => setDialog("new")} className="flex items-center gap-2 px-4 py-2 bg-[#1A237E] text-white text-sm font-semibold rounded-md hover:bg-[#121A5C]" data-testid="add-review-button">
          <Plus className="h-4 w-4" /> Add Review
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reviews-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                {COLUMNS.map((col) => (
                  <th key={col.key} className="px-4 py-3">
                    <button onClick={() => toggleSort(col.key)} className="flex items-center gap-1 hover:text-slate-800" data-testid={`sort-${col.key}`}>
                      {col.label} <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                ))}
                <th className="px-4 py-3">Review</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="reviews-loading">Loading…</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="reviews-empty">No reviews logged yet.</td></tr>
              ) : (
                sorted.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`review-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{r.reviewer_name}</td>
                    <td className="px-4 py-3 text-slate-600">{r.platform}</td>
                    <td className="px-4 py-3"><Stars rating={r.rating} /></td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.date || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[320px] truncate">{r.text}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setDialog(r)} className="p-1.5 text-slate-400 hover:text-[#1A237E]" aria-label="Edit" data-testid={`review-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(r)} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Delete" data-testid={`review-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {dialog && (
        <ReviewDialog review={dialog === "new" ? null : dialog} onClose={() => setDialog(null)} onSaved={load} />
      )}
    </div>
  );
}
